import { colors } from "./theme.js";
/** Centralised display config for all audit log event types (sidecar). */
export const EVENT_STYLES = {
    // ── Session lifecycle ─────────────────────────────────────────────────
    "session.created": { color: colors.primary, label: "Session created", icon: "plus" },
    "session.planned": { color: colors.primary, label: "Session planned", icon: "clock" },
    "session.booked": { color: colors.success, label: "Session booked", icon: "check" },
    "session.skipped": { color: colors.textSecondary, label: "Session skipped", icon: "info" },
    "session.failed": { color: colors.danger, label: "Session failed", icon: "x" },
    "session.cancelled": { color: colors.warning, label: "Session cancelled", icon: "x" },
    // ── Court lifecycle ───────────────────────────────────────────────────
    "court.planned": { color: colors.primary, label: "Court planned", icon: "plus" },
    "court.booked": { color: colors.success, label: "Court booked", icon: "check" },
    "court.cancelled": { color: colors.warning, label: "Court cancelled", icon: "x" },
    "court.excluded": { color: colors.amber, label: "Court unavailable", icon: "alert-triangle" },
    "court.lease_failed": { color: colors.danger, label: "Lease failed", icon: "x" },
    "court.confirm_failed": { color: colors.danger, label: "Confirm failed", icon: "x" },
    // ── Venue ─────────────────────────────────────────────────────────────
    "venue.dropped": { color: colors.danger, label: "Venue dropped", icon: "alert-triangle" },
    // ── Cap / quota ───────────────────────────────────────────────────────
    "cap.suppressed": { color: colors.textSecondary, label: "Cap limit applied", icon: "info" },
    "cap.evaluated": { color: colors.slate, label: "Cap evaluated", icon: "info" },
    // ── Rules ─────────────────────────────────────────────────────────────
    "rule.created": { color: colors.indigo, label: "Rule created", icon: "plus" },
    "rule.updated": { color: colors.indigo, label: "Rule updated", icon: "refresh" },
    "rule.deleted": { color: colors.indigo, label: "Rule deleted", icon: "trash" },
    "rule.skipped": { color: colors.textSecondary, label: "Rule skipped", icon: "info" },
    // ── Credentials & permissions ─────────────────────────────────────────
    "credential.linked": { color: colors.purple, label: "Credential linked", icon: "key" },
    "credential.unlinked": { color: colors.purple, label: "Credential unlinked", icon: "key" },
    "credential.refreshed": { color: colors.purple, label: "Token refreshed", icon: "refresh" },
    "permission.granted": { color: colors.warning, label: "Permission granted", icon: "check" },
    "permission.revoked": { color: colors.warning, label: "Permission revoked", icon: "x" },
    // ── Credits ───────────────────────────────────────────────────────────
    "credit.synced": { color: colors.teal, label: "Credits synced", icon: "refresh" },
    "credit.low": { color: colors.warning, label: "Credits low", icon: "alert-triangle" },
    // ── System ────────────────────────────────────────────────────────────
    "system.login": { color: colors.slate, label: "Login", icon: "key" },
    "system.error": { color: colors.danger, label: "System error", icon: "alert-triangle" },
};
/**
 * Friendly badge label + colour for an audit event type. Single source of truth
 * for every table/timeline that renders an event-type pill. Prefers EVENT_STYLES
 * (short human labels like "Cap limit applied"); falls back to a prefix-derived
 * colour and the raw type for any event not yet in the registry.
 */
export function eventTypeBadge(type) {
    const style = EVENT_STYLES[type];
    if (style)
        return { label: style.label, color: style.color };
    // Fallback colour by namespace prefix (keeps unknown/new events readable).
    let color = colors.textSecondary;
    if (type.startsWith("rule."))
        color = colors.indigo;
    else if (type.startsWith("permission."))
        color = colors.warning;
    else if (type.startsWith("credential."))
        color = colors.purple;
    else if (type.startsWith("session."))
        color = colors.primary;
    else if (type.startsWith("credit."))
        color = colors.teal;
    else if (type.endsWith(".failed") || type.endsWith(".error"))
        color = colors.danger;
    return { label: type, color };
}
/**
 * Filter groups for the Audit page.
 * Each group has a display label and the list of event types it covers.
 * The "all" group has no `types` array — it shows everything.
 */
export const EVENT_FILTER_GROUPS = [
    { key: "all", label: "All" },
    {
        key: "booking",
        label: "Booking",
        types: [
            "court.planned",
            "court.booked",
            "court.cancelled",
            "court.excluded",
            "court.lease_failed",
            "court.confirm_failed",
            "venue.dropped",
            "cap.suppressed",
            "cap.evaluated",
        ],
    },
    {
        key: "sessions",
        label: "Sessions",
        types: [
            "session.created",
            "session.planned",
            "session.booked",
            "session.skipped",
            "session.failed",
            "session.cancelled",
        ],
    },
    {
        key: "rules",
        label: "Rules",
        types: ["rule.created", "rule.updated", "rule.deleted", "rule.skipped"],
    },
    {
        key: "credentials",
        label: "Credentials",
        types: [
            "credential.linked",
            "credential.unlinked",
            "credential.refreshed",
            "permission.granted",
            "permission.revoked",
        ],
    },
    {
        key: "credits",
        label: "Credits",
        types: ["credit.synced", "credit.low"],
    },
    {
        key: "system",
        label: "System",
        types: ["system.login", "system.error"],
    },
];
